function isNodeRef(value) {
    return (typeof value === "object" &&
        value !== null &&
        "nodeId" in value &&
        typeof value.nodeId === "string");
}
function collectNodeRefs(expr) {
    if (!expr)
        return [];
    const refs = [];
    const visit = (operand) => {
        if (operand === undefined || operand === null)
            return;
        if (isNodeRef(operand)) {
            refs.push(operand);
            return;
        }
        if (typeof operand === "object" && "op" in operand) {
            visit(operand.operand);
            for (const nested of operand.operands ?? [])
                visit(nested);
        }
    };
    visit(expr.operand);
    for (const operand of expr.operands ?? [])
        visit(operand);
    return refs;
}
function addReference(idx, nodeId, referencerId) {
    const list = idx.referencesByTargetNode.get(nodeId) ?? [];
    if (!list.includes(referencerId))
        list.push(referencerId);
    idx.referencesByTargetNode.set(nodeId, list);
}
function addDependency(idx, nodeId, validatorId) {
    const list = idx.validatorsByDependency.get(nodeId) ?? [];
    if (!list.includes(validatorId))
        list.push(validatorId);
    idx.validatorsByDependency.set(nodeId, list);
}
function registerChild(idx, node, parentId, path) {
    idx.nodeById.set(node.id, node);
    idx.parentById.set(node.id, parentId);
    idx.pathByNode.set(node.id, path);
    idx.nodeByPath.set(path, node.id);
    const siblings = idx.childrenByParent.get(parentId) ?? [];
    siblings.push(node.id);
    idx.childrenByParent.set(parentId, siblings);
}
function walk(node, parentId, parentPath, idx) {
    const path = parentPath === "" ? node.name : `${parentPath}.${node.name}`;
    registerChild(idx, node, parentId, path);
    descend(node, path, idx);
}
/**
 * A collection's row template is path-transparent: it represents the row shape, not a named field.
 *
 * The row's key or index is chosen while the form runs, so everything under the template shares the
 * collection's own path — which is what makes a template's descendants addressable by the editor
 * without inventing a row that does not exist yet.
 */
function walkRowTemplate(node, collectionId, collectionPath, idx) {
    idx.nodeById.set(node.id, node);
    idx.parentById.set(node.id, collectionId);
    idx.pathByNode.set(node.id, collectionPath);
    const siblings = idx.childrenByParent.get(collectionId) ?? [];
    siblings.push(node.id);
    idx.childrenByParent.set(collectionId, siblings);
    if (node.node === "group") {
        for (const child of node.children)
            walk(child, node.id, collectionPath, idx);
    }
    else if (node.node === "array" || node.node === "record") {
        walkRowTemplate(node.item, node.id, collectionPath, idx);
    }
}
function descend(node, path, idx) {
    if (node.node === "group") {
        for (const child of node.children)
            walk(child, node.id, path, idx);
    }
    else if (node.node === "array" || node.node === "record") {
        walkRowTemplate(node.item, node.id, path, idx);
    }
}
export function buildIndexes(project) {
    const idx = {
        nodeById: new Map(),
        parentById: new Map(),
        childrenByParent: new Map(),
        pathByNode: new Map(),
        nodeByPath: new Map(),
        referencesByTargetNode: new Map(),
        validatorsByDependency: new Map(),
    };
    const root = project.schema;
    idx.nodeById.set(root.id, root);
    idx.parentById.set(root.id, null);
    idx.pathByNode.set(root.id, "");
    idx.nodeByPath.set("", root.id);
    descend(root, "", idx);
    for (const validator of project.formValidators) {
        for (const dep of validator.dependencies) {
            addReference(idx, dep.nodeId, validator.id);
            addDependency(idx, dep.nodeId, validator.id);
        }
        for (const ref of collectNodeRefs(validator.condition)) {
            addReference(idx, ref.nodeId, validator.id);
        }
        if (validator.errorTarget)
            addReference(idx, validator.errorTarget.nodeId, validator.id);
    }
    const visitFieldServerValidators = (node) => {
        if (node.node === "field" && node.serverValidator) {
            const sv = node.serverValidator;
            for (const dep of sv.dependencies) {
                addReference(idx, dep.nodeId, sv.id);
                addDependency(idx, dep.nodeId, sv.id);
            }
            for (const ref of collectNodeRefs(sv.skipWhen))
                addReference(idx, ref.nodeId, sv.id);
        }
        if (node.node === "group") {
            for (const child of node.children)
                visitFieldServerValidators(child);
        }
        else if (node.node === "array" || node.node === "record") {
            visitFieldServerValidators(node.item);
        }
    };
    visitFieldServerValidators(root);
    for (const ref of project.behaviors.draft?.exclude ?? []) {
        addReference(idx, ref.nodeId, "behaviors.draft");
    }
    return idx;
}
